import { MenuContextProvider } from "@ubnt/store.core/lib/contexts/MenuContext";
import { MicrositeMenuContextProvider } from "@ubnt/store.core/lib/contexts/MicrositeMenuContext";
import { SiteId } from "@ubnt/store.core/lib/contexts/MenuContext/mapping";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import React, { useEffect } from "react";
import { MicrositeProductPageContextProvider } from "@ubnt/store.core/lib/components/Microsites/context/MicrositeProductPageContext";
import { GlobalHeader } from "../Header";
import { useSiteSettings } from "../../context/SiteSettingsContext/context";
import { Footer } from "../Footer";
import {
  MainContainer,
  SectionContainer,
  MainContainerContent,
} from "./layout";
import "../../styles/fonts.scss";
import {
  REGIONAL_MICROSITE_PREFIX_PATH,
  LANGUAGE_PATH,
} from "hooks/useIsNewDesign";
import { GLOBAL_SITE_CONFIG } from "context/SiteSettingsContext/constants";
import { getPageBasePaths } from "context/SiteSettingsContext/utils";
import { getLanguageDirection } from "@ubnt/store.core/lib/contexts/MenuContext/utils";
import { VwoDebug } from "components/VwoDebug";
import { useBuyButton } from "hooks/useBuyButton";

// #############################################################################
// Pages
// #############################################################################
// import CameraSecurity from "pages/Routes/CameraSecurity";
import CameraSecurityBullet from "pages/Routes/CameraSecurity/Bullet";
import CameraSecurityDomeTurret from "pages/Routes/CameraSecurity/DomeTurret";
// import CameraSecurityCompact from "pages/Routes/CameraSecurity/Compact";
// import CameraSecurityAITheta from "pages/Routes/CameraSecurity/AITheta";
import CameraSecurityPtz from "pages/Routes/CameraSecurity/Ptz";
import CameraSecuritySpecialDevices from "pages/Routes/CameraSecurity/SpecialDevices";
import CameraSecurityVideoRecorders from "pages/Routes/CameraSecurity/VideoRecorders";
import PhysicalSecurityOverview from "pages/Routes/PhysicalSecurityOverview";
import CloudGatewaysOverview from "pages/Routes/CloudGateways";
import CloudGatewaysEnterpriseScale from "pages/Routes/CloudGateways/EnterpriseScale";
import CloudGatewaysLargeScale from "pages/Routes/CloudGateways/LargeScale";
import CloudGatewaysWiFiIntegrated from "pages/Routes/CloudGateways/WiFiIntegrated";
import CloudGatewaysCompact from "pages/Routes/CloudGateways/Compact";
import Landing from "pages/Routes/Landing";
import HowItWorks from "pages/Routes/HowItWorks";
import UICare from "pages/Routes/UICare";
import NotFound from "pages/NotFound/index.mjs";
import ResourceCalculator from "pages/Routes/CloudGateways/ResourceCalculator";
import UID from "pages/Routes/Identity";
import WiFi from "pages/Routes/WiFi/WiFiOverview";
import WiFiEnterprise from "pages/Routes/WiFi/Enterprise";
import WiFiFlagship from "pages/Routes/WiFi/Flagship";
import WiFiWall from "pages/Routes/WiFi/InWall";
import WiFiOutdoor from "pages/Routes/WiFi/FlexibleOutdoor";
import WiFiBridging from "pages/Routes/WiFi/Bridging";
import IntegrationsMobileRoutingV2 from "pages/Routes/Integrations/MobileRouting";
import IntegrationsManagedVoIP from "pages/Routes/Integrations/ManagedVoIP";
import IntegrationsPremiumIoT from "pages/Routes/Integrations/PremiumIoT";
import IntegrationsNetworkStorage from "pages/Routes/Integrations/NetworkStorage";
import IntegrationsCloudKeysGateways from "pages/Routes/Integrations/CloudKeysGateways";
import DownloadStarted from "pages/Routes/DownloadStarted";
import Download from "pages/Routes/Download/Landing";
import ProductDownload from "pages/Routes/Download/Product";
import PastReleases from "pages/Routes/Download/PastReleases";
import ProductGroup from "pages/Routes/Download/ProductGroup";
import SwitchingOverview from "pages/Routes/Switching/Overview";
import SwitchingEnterprise from "pages/Routes/Switching/Enterprise";
import SwitchingProfessional from "pages/Routes/Switching/Professional";
import SwitchingStandard from "pages/Routes/Switching/Standard";
import SwitchingCompact from "pages/Routes/Switching/Compact";
import SwitchingWan from "pages/Routes/Switching/Wan";
import AccessoryTech from "pages/Routes/AccessoryTech";
import DoorAccess from "pages/Routes/DoorAccess";
import SiteSupport from "pages/Routes/SiteSupport";
import Wifiman from "pages/Routes/Download/Wifiman";
import IdentityDownload from "pages/Routes/Download/Identity";
import IdentityAppleDownload from "pages/Routes/Download/IdentityApple";
import IdentityAndroidDownload from "pages/Routes/Download/IdentityAndroid";
import Ultra from "pages/Routes/Ultra";
import ProfessionalIntegrators from "pages/Routes/ProfessionalIntegrators";
import TrustCenter from "pages/Routes/TrustCenter";
import WhatsNew from "pages/Routes/WhatsNew";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { UniFiPartnerHub } from "pages/Sections/UniFiPartnerHub/UniFiPartnerHub";

gsap.registerPlugin(useGSAP);

const App = () => {
  const location = useLocation();
  const { store, language, isAppView } = useSiteSettings();
  const { showBuyButton } = useBuyButton();

  // #############################################################################
  // Used to remove focus from the active element (in navigation) when the location changes
  // #############################################################################
  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [location]);

  // #############################################################################
  // Update `<html lang="...">`  on language change, right now it always EN on first render
  // #############################################################################
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
  }, [language]);

  // #############################################################################
  // Render
  // #############################################################################
  return (
    <MenuContextProvider
      pathname={location.pathname}
      hash={location.hash}
      storeId={store.id}
      context={SiteId.Ui}
      globalSiteConfig={GLOBAL_SITE_CONFIG}
      language={language}
    >
      <MicrositeMenuContextProvider
        pathname={location.pathname}
        hash={location.hash}
        storeId={store.id}
        context={SiteId.Ui}
        globalSiteConfig={GLOBAL_SITE_CONFIG}
        language={language}
      >
        <MicrositeProductPageContextProvider showBuyButton={showBuyButton}>
          <VwoDebug />
          <MainContainer>
            <MainContainerContent>
              {/* Hide the global footer when microsite is embed in WebView for example, in UniFi Assistant mobile app */}
              {!isAppView && <GlobalHeader />}
              <SectionContainer dir={getLanguageDirection(language)}>
                <Switch>
                  {/* TODO: what's the purpose of this? */}
                  <Redirect
                    from="/:url*(/+)"
                    to={window.location.pathname.slice(0, -1)}
                  />
                  <Route
                    path={getPageBasePaths("/")}
                    exact
                    render={() => <Landing />}
                  />
                  <Route
                    path={getPageBasePaths("/how-it-works")}
                    render={() => <HowItWorks />}
                  />
                  <Route
                    path={getPageBasePaths("/ui-care")}
                    exact
                    render={() => <UICare />}
                  />
                  <Route
                    path={getPageBasePaths("/whats-new")}
                    exact
                    render={() => <WhatsNew />}
                  />
                  {/**
                   * This /consoles/* redirects might not be actually needed but added here for redundancy.
                   * The same ones are added in nginx and viewer-request.json configs as well.
                   * These could be remove later once the new infra is in place.
                   */}
                  <Redirect exact from="/consoles" to="/cloud-gateways" />
                  <Redirect
                    exact
                    from={`${REGIONAL_MICROSITE_PREFIX_PATH}${LANGUAGE_PATH}/consoles`}
                    to={`${REGIONAL_MICROSITE_PREFIX_PATH}${LANGUAGE_PATH}/cloud-gateways`}
                  />
                  <Redirect
                    from="/consoles/resource-calculator"
                    to="/cloud-gateways/resource-calculator"
                  />
                  <Route
                    path={getPageBasePaths(
                      "/cloud-gateways/resource-calculator",
                    )}
                    exact
                    render={() => <ResourceCalculator />}
                  />
                  <Route
                    path={getPageBasePaths("/cloud-gateways")}
                    exact
                    render={() => <CloudGatewaysOverview />}
                  />
                  <Route
                    path={getPageBasePaths("/cloud-gateways/enterprise-scale")}
                    exact
                    render={() => <CloudGatewaysEnterpriseScale />}
                  />
                  <Route
                    path={getPageBasePaths("/cloud-gateways/large-scale")}
                    exact
                    render={() => <CloudGatewaysLargeScale />}
                  />
                  <Route
                    path={getPageBasePaths("/cloud-gateways/wifi-integrated")}
                    exact
                    render={() => <CloudGatewaysWiFiIntegrated />}
                  />
                  {/* TODO: CHECK IF THESE ARE NECESSARY */}
                  <Route
                    path={getPageBasePaths("/cloud-gateways/compact")}
                    exact
                    render={() => <CloudGatewaysCompact />}
                  />
                  <Route
                    path={getPageBasePaths("/ultra")}
                    exact
                    render={() => <Ultra />}
                  />
                  <Route
                    path={getPageBasePaths("/wifi")}
                    exact
                    render={() => <WiFi />}
                  />
                  <Route
                    path={getPageBasePaths("/wifi/flagship")}
                    exact
                    render={() => <WiFiFlagship />}
                  />
                  <Route
                    path={getPageBasePaths("/wifi/enterprise")}
                    exact
                    render={() => <WiFiEnterprise />}
                  />
                  <Route
                    path={getPageBasePaths("/wifi/wall")}
                    exact
                    render={() => <WiFiWall />}
                  />
                  <Route
                    path={getPageBasePaths("/wifi/outdoor")}
                    exact
                    render={() => <WiFiOutdoor />}
                  />
                  <Route
                    path={getPageBasePaths("/wifi/bridging")}
                    exact
                    render={() => <WiFiBridging />}
                  />
                  <Route
                    path={getPageBasePaths("/switching")}
                    exact
                    render={() => <SwitchingOverview />}
                  />
                  <Route
                    path={getPageBasePaths("/switching/enterprise")}
                    exact
                    render={() => <SwitchingEnterprise />}
                  />
                  <Route
                    path={getPageBasePaths("/switching/professional")}
                    exact
                    render={() => <SwitchingProfessional />}
                  />
                  <Route
                    path={getPageBasePaths("/switching/value")}
                    exact
                    render={() => <SwitchingStandard />}
                  />
                  <Route
                    path={getPageBasePaths("/switching/compact")}
                    exact
                    render={() => <SwitchingCompact />}
                  />
                  <Route
                    path={getPageBasePaths("/switching/wan")}
                    exact
                    render={() => <SwitchingWan />}
                  />
                  <Route
                    path={getPageBasePaths("/physical-security")}
                    exact
                    render={() => <PhysicalSecurityOverview />}
                  />
                  <Route
                    path={getPageBasePaths("/physical-security/bullet")}
                    exact
                    render={() => <CameraSecurityBullet />}
                  />
                  <Route
                    path={getPageBasePaths("/physical-security/dome-turret")}
                    exact
                    render={() => <CameraSecurityDomeTurret />}
                  />
                  <Route
                    path={getPageBasePaths("/physical-security/ptz")}
                    exact
                    render={() => <CameraSecurityPtz />}
                  />
                  <Route
                    path={getPageBasePaths(
                      "/physical-security/special-devices",
                    )}
                    render={() => <CameraSecuritySpecialDevices />}
                  />
                  <Route
                    path={getPageBasePaths(
                      "/physical-security/video-recorders",
                    )}
                    exact
                    render={() => <CameraSecurityVideoRecorders />}
                  />
                  <Route
                    path={[
                      ...getPageBasePaths(
                        "/download/unifi/mac/download-started",
                      ),
                      ...getPageBasePaths(
                        "/download/unifi/win/download-started",
                      ),
                      ...getPageBasePaths(
                        "/download/unifi/linux/download-started",
                      ),
                      // TODO: Ask @ui-toms-mengots or @oskar-rosen-ui if these are needed
                      ...getPageBasePaths(
                        "/download/unifi/unifi-os/download-started",
                      ),
                      ...getPageBasePaths(
                        "/download/unifi/unifi-os-native/download-started",
                      ),
                    ]}
                    exact
                    render={() => <DownloadStarted />}
                  />
                  <Redirect
                    from="/door-access"
                    to="/physical-security/door-access"
                  />
                  <Route
                    path={getPageBasePaths("/physical-security/door-access")}
                    render={() => <DoorAccess />}
                  />
                  <Redirect
                    from="/door-access/control-hubs"
                    to="/physical-security/door-access"
                  />
                  <Redirect
                    from="/door-access/readers"
                    to="/physical-security/door-access"
                  />
                  <Redirect
                    from="/door-access/intercoms"
                    to="/physical-security/door-access/intercoms"
                  />
                  <Redirect
                    from="/door-access/locks-accessories"
                    to="/physical-security/door-access"
                  />
                  <Redirect
                    from="/door-access/locks-accessories"
                    to="/physical-security/door-access/touch-pass"
                  />

                  {/**
                   * Integrations redirects
                   */}
                  {/* NOTE: Previously was /phone-system -> /smart-integrations */}
                  <Redirect
                    from="/phone-system"
                    to="/integrations/managed-voip"
                  />
                  <Redirect
                    exact
                    from="/integrations"
                    to="/integrations/managed-voip"
                  />
                  <Redirect
                    exact
                    from={`${REGIONAL_MICROSITE_PREFIX_PATH}/integrations`}
                    to={`${REGIONAL_MICROSITE_PREFIX_PATH}/integrations/managed-voip`}
                  />
                  <Redirect
                    exact
                    from={`${REGIONAL_MICROSITE_PREFIX_PATH}${LANGUAGE_PATH}/integrations`}
                    to={`${REGIONAL_MICROSITE_PREFIX_PATH}${LANGUAGE_PATH}/integrations/managed-voip`}
                  />
                  <Route
                    path={[
                      ...getPageBasePaths("/integrations/managed-voip"),
                      ...getPageBasePaths(
                        "/integrations/managed-voip/international-rates",
                      ),
                    ]}
                    exact
                    render={() => <IntegrationsManagedVoIP />}
                  />
                  <Route
                    path={getPageBasePaths("/integrations/mobile-routing")}
                    exact
                    render={() => <IntegrationsMobileRoutingV2 />}
                  />
                  <Route
                    path={getPageBasePaths("/integrations/premium-iot")}
                    render={() => <IntegrationsPremiumIoT />}
                  />
                  <Route
                    path={getPageBasePaths("/integrations/network-storage")}
                    exact
                    render={() => <IntegrationsNetworkStorage />}
                  />
                  <Route
                    path={getPageBasePaths("/integrations/cloud-keys-gateways")}
                    render={() => <IntegrationsCloudKeysGateways />}
                  />
                  <Redirect
                    exact
                    from="/accessory-tech/cloud-keys-gateways"
                    to="/integrations/cloud-keys-gateways/control-plane-options"
                  />
                  <Redirect
                    exact
                    from={`${REGIONAL_MICROSITE_PREFIX_PATH}/accessory-tech/cloud-keys-gateways`}
                    to={`${REGIONAL_MICROSITE_PREFIX_PATH}/integrations/cloud-keys-gateways/control-plane-options"`}
                  />
                  <Redirect
                    exact
                    from={`${REGIONAL_MICROSITE_PREFIX_PATH}${LANGUAGE_PATH}/accessory-tech/cloud-keys-gateways`}
                    to={`${REGIONAL_MICROSITE_PREFIX_PATH}${LANGUAGE_PATH}/integrations/cloud-keys-gateways/control-plane-options"`}
                  />
                  <Redirect from="/unifi-accessories" to="/accessory-tech" />
                  <Route
                    path={getPageBasePaths("/integrations/accessory-tech")}
                    render={() => <AccessoryTech />}
                  />
                  <Route
                    path={getPageBasePaths("/professional-integrators")}
                    exact
                    render={() => <ProfessionalIntegrators />}
                  />
                  <Route
                    path={getPageBasePaths("/site-support")}
                    exact
                    render={() => <SiteSupport />}
                  />
                  <Route
                    render={() => <UniFiPartnerHub />}
                    path={[
                      ...getPageBasePaths("/partner-hub"),
                      ...getPageBasePaths(
                        "/partner-hub/partner-program",
                      ),
                    ]}
                    exact
                  />
                  <Route
                    path={getPageBasePaths("/trust-center")}
                    exact
                    render={() => <TrustCenter />}
                  />
                  <Route
                    path={getPageBasePaths("/download")}
                    exact
                    render={() => <Download />}
                  />
                  <Route
                    path={getPageBasePaths("/download/software/:slug")}
                    exact
                    render={() => <ProductDownload />}
                  />
                  <Route
                    path={getPageBasePaths("/download/releases/:slug")}
                    exact
                    render={() => <PastReleases />}
                  />
                  <Route
                    path={getPageBasePaths("/download/:slug")}
                    exact
                    render={() => <ProductGroup />}
                  />
                  <Route
                    path={getPageBasePaths("/download/app/wifiman-desktop")}
                    exact
                    render={() => <Wifiman />}
                  />
                  <Route
                    path={getPageBasePaths("/download/app/identity-desktop")}
                    exact
                    render={() => <IdentityDownload />}
                  />
                  <Route
                    path={getPageBasePaths("/download/app/identity-ios")}
                    exact
                    render={() => <IdentityAppleDownload />}
                  />
                  <Route
                    path={getPageBasePaths("/download/app/identity-android")}
                    exact
                    render={() => <IdentityAndroidDownload />}
                  />
                  <Route
                    path="/uisp"
                    exact
                    render={() => {
                      window.location.href = "https://uisp.com";
                      return null;
                    }}
                  />
                  <Route
                    path="/uisp/ptp-bridging"
                    exact
                    render={() => {
                      window.location.href =
                        "https://uisp.com/wireless/airfiber-and-ltu/airfiber";
                      return null;
                    }}
                  />
                  <Route
                    path="/uisp/multipoint"
                    exact
                    render={() => {
                      window.location.href =
                        "https://uisp.com/wireless/airfiber-and-ltu/ltu";
                      return null;
                    }}
                  />
                  <Route
                    path="/uisp/wired-technologies"
                    exact
                    render={() => {
                      window.location.href = "https://uisp.com/wired";
                      return null;
                    }}
                  />
                  <Route
                    path="/uisp/accessories"
                    exact
                    render={() => {
                      window.location.href = "https://uisp.com/accessories";
                      return null;
                    }}
                  />
                  <Route
                    path="/uisp/*"
                    exact
                    render={() => {
                      window.location.href = "https://uisp.ui.com";
                      return null;
                    }}
                  />
                  <Route
                    path={[
                      ...getPageBasePaths("/identity"),
                      /**
                       * Special case/request for the desktop QR code pairing page
                       * @see https://ui.slack.com/archives/GMDLV5QPN/p1747722286236019?thread_ts=1742182262.626689&cid=GMDLV5QPN
                       */
                      "/identity/qrcode/desktop/bt_pairing",
                    ]}
                    exact
                    render={() => <UID />}
                  />
                  <Route
                    exact
                    path="/(.+)"
                    render={() => (
                      <Switch>
                        <Route
                          exact
                          render={() => <NotFound language={language} />}
                        />
                      </Switch>
                    )}
                  />
                </Switch>
              </SectionContainer>
            </MainContainerContent>

            {/* Hide the footer when microsite is embed in WebView for example, in UniFi Assistant mobile app */}
            {!isAppView && <Footer />}
          </MainContainer>
        </MicrositeProductPageContextProvider>
      </MicrositeMenuContextProvider>
    </MenuContextProvider>
  );
};

export default App;
